import { ERROR_PANEL_DEFAULT_NOTE } from "../../../components/common/ErrorPanel/ErrorPanel";

export const CONNECTION_ERROR_CODES = {
  VALIDATION_FAILED: "CONN_1000",
  EMAIL_REQUIRED: "CONN_1001",
  PASSWORD_REQUIRED: "CONN_1002",
  INVALID_EMAIL: "CONN_1003",
  OTP_MODE_REQUIRED: "CONN_1004",
  INBOX_OWNER_REQUIRED: "CONN_1005",
  NO_CHANNEL_SELECTED: "CONN_1006",
  PASSWORD_NOT_ENCRYPTED: "CONN_1008",
  OTP_MODE_NOT_SUPPORTED: "CONN_1009",
  PASSWORD_NOT_SUPPORTED: "CONN_1010",
  NOTHING_TO_UPDATE: "CONN_1011",
  CHANNEL_NOT_FOUND: "CONN_1101",
  CHANNEL_NOT_SUPPORTED: "CONN_1102",
  CONNECTION_NOT_FOUND: "CONN_2001",
  INVALID_STATUS_ACTION: "CONN_2002",
  EMAIL_LOCKED: "CONN_2003",
  VERIFICATION_IN_PROGRESS: "CONN_2004",
  SUB_BRAND_REQUIRED: "CONN_2005",
  SUB_BRAND_UNKNOWN: "CONN_2006",
  DUPLICATE_ACCOUNT_EMAIL: "CONN_2007",
  INBOX_NOT_FOUND: "CONN_2101",
  FORWARDING_NOT_CONFIRMED: "CONN_2102",
  FILTER_NOT_CONFIRMED: "CONN_2103",
  PROVIDER_UNKNOWN: "CONN_2104",
  INBOX_DELEGATED: "CONN_2105",
  PROVIDER_NOT_SUPPORTED: "CONN_2106",
  STEP_NOT_APPLICABLE: "CONN_2107",
  CHANNELS_NOT_IN_INBOX: "CONN_2108",
  VERIFICATION_START_FAILED: "CONN_3001",
  FORWARDING_CHECK_START_FAILED: "CONN_3002",
  VERIFICATION_TIMEOUT: "CONN_3003",
  CATALOG_UNAVAILABLE: "CONN_3004",
  DISCONNECT_FAILED: "CONN_3005",
  CREDENTIAL_SAVE_FAILED: "CONN_3006",
  LINK_EXPIRED: "CONN_4001",
  LINK_INVALID: "CONN_4002",
  LINK_NOT_FOUND: "CONN_4003",
  RATE_LIMITED: "CONN_4004",
  NOT_AUTHORIZED: "CONN_5001",
  INTERNAL_ERROR: "CONN_9001",
  NOT_IMPLEMENTED: "CONN_9002",
  NETWORK_ERROR: "CONNECTION_NETWORK_ERROR",
  UNKNOWN_ERROR: "CONNECTION_UNKNOWN_ERROR",
} as const;

export type ConnectionErrorCode =
  (typeof CONNECTION_ERROR_CODES)[keyof typeof CONNECTION_ERROR_CODES];

export type ConnectionErrorCategory =
  | "validation"
  | "conflict"
  | "transport"
  | "authorization"
  | "expired-link"
  | "rate-limit"
  | "server";

export interface ConnectionErrorDefinition {
  code: ConnectionErrorCode;
  message: string;
  category: ConnectionErrorCategory;
  retryable: boolean;
  /** What to do next — the error panel's second line. Falls back to the category's generic line. */
  nextStep?: string;
}

const definition = (
  code: ConnectionErrorCode,
  message: string,
  category: ConnectionErrorCategory,
  retryable = false,
  nextStep?: string
): ConnectionErrorDefinition => ({ code, message, category, retryable, ...(nextStep ? { nextStep } : {}) });

export const CONNECTION_ERRORS: ConnectionErrorDefinition[] = [
  definition(CONNECTION_ERROR_CODES.VALIDATION_FAILED, "Invalid request", "validation"),
  definition(CONNECTION_ERROR_CODES.EMAIL_REQUIRED, "Email is required for this channel", "validation"),
  definition(CONNECTION_ERROR_CODES.PASSWORD_REQUIRED, "Password is required for this channel", "validation"),
  definition(CONNECTION_ERROR_CODES.INVALID_EMAIL, "Invalid email format", "validation"),
  definition(CONNECTION_ERROR_CODES.OTP_MODE_REQUIRED, "OTP mode is required for this channel", "validation"),
  definition(CONNECTION_ERROR_CODES.INBOX_OWNER_REQUIRED, "Inbox owner is required for this channel", "validation"),
  definition(CONNECTION_ERROR_CODES.NO_CHANNEL_SELECTED, "At least one channel must be selected", "validation"),
  definition(CONNECTION_ERROR_CODES.PASSWORD_NOT_ENCRYPTED, "Password must be encrypted before saving", "validation"),
  definition(CONNECTION_ERROR_CODES.OTP_MODE_NOT_SUPPORTED, "OTP mode is not supported for this channel", "validation"),
  definition(CONNECTION_ERROR_CODES.PASSWORD_NOT_SUPPORTED, "This channel does not use a password", "validation"),
  definition(CONNECTION_ERROR_CODES.NOTHING_TO_UPDATE, "Nothing to update", "validation"),
  definition(CONNECTION_ERROR_CODES.CHANNEL_NOT_FOUND, "Channel not found or inactive", "validation"),
  definition(CONNECTION_ERROR_CODES.CHANNEL_NOT_SUPPORTED, "This channel does not support credential connect", "validation"),
  definition(CONNECTION_ERROR_CODES.CONNECTION_NOT_FOUND, "Connection not found", "validation"),
  definition(CONNECTION_ERROR_CODES.INVALID_STATUS_ACTION, "Action not allowed in the current status", "conflict"),
  definition(CONNECTION_ERROR_CODES.EMAIL_LOCKED, "Email cannot be changed after forwarding is set up", "conflict", false, "Add a new account with the right email, then remove this one."),
  definition(CONNECTION_ERROR_CODES.VERIFICATION_IN_PROGRESS, "Verification already in progress", "conflict", false, "Wait for the current check to finish before trying again."),
  definition(CONNECTION_ERROR_CODES.SUB_BRAND_REQUIRED, "At least one sub-brand must be selected", "validation"),
  definition(CONNECTION_ERROR_CODES.SUB_BRAND_UNKNOWN, "Selected sub-brand is not available for this account", "validation"),
  definition(CONNECTION_ERROR_CODES.DUPLICATE_ACCOUNT_EMAIL, "Another account on this channel already uses this email", "conflict", false, "Use a different email, or remove the existing account first."),
  definition(CONNECTION_ERROR_CODES.INBOX_NOT_FOUND, "Inbox not found", "validation"),
  definition(CONNECTION_ERROR_CODES.FORWARDING_NOT_CONFIRMED, "The forwarding or the filter isn\u2019t set correctly.", "conflict", false, "Click on the \u201cOpen Forwarding Settings\u201d button below and follow the steps."),
  definition(CONNECTION_ERROR_CODES.FILTER_NOT_CONFIRMED, "The forwarding or the filter isn\u2019t set correctly.", "conflict", false, "Click on the \u201cUpdate Filter Settings\u201d button below and follow the steps."),
  definition(CONNECTION_ERROR_CODES.PROVIDER_UNKNOWN, "Email provider could not be identified", "validation"),
  definition(CONNECTION_ERROR_CODES.INBOX_DELEGATED, "This inbox is set up by its owner through the setup link", "conflict", false, "Ask them to finish the setup, or resend the link."),
  definition(CONNECTION_ERROR_CODES.PROVIDER_NOT_SUPPORTED, "This email provider isn’t supported yet", "conflict", false, "Choose Gmail or Outlook if your inbox is on one of them."),
  definition(CONNECTION_ERROR_CODES.STEP_NOT_APPLICABLE, "This step isn’t part of this provider’s setup", "conflict"),
  definition(CONNECTION_ERROR_CODES.CHANNELS_NOT_IN_INBOX, "Selected channels don’t belong to this inbox", "validation"),
  definition(CONNECTION_ERROR_CODES.VERIFICATION_START_FAILED, "Could not start verification with the connector service", "transport", true),
  definition(CONNECTION_ERROR_CODES.FORWARDING_CHECK_START_FAILED, "Could not start forwarding check with the connector service", "transport", true),
  definition(CONNECTION_ERROR_CODES.VERIFICATION_TIMEOUT, "Verification timed out", "transport", true),
  definition(CONNECTION_ERROR_CODES.CATALOG_UNAVAILABLE, "Channel catalogue is unavailable", "transport", true),
  definition(CONNECTION_ERROR_CODES.DISCONNECT_FAILED, "Could not disconnect the linked account", "transport", true, "Nothing was removed. Please try again in a moment."),
  definition(CONNECTION_ERROR_CODES.CREDENTIAL_SAVE_FAILED, "We couldn’t save the credential", "transport", true),
  definition(CONNECTION_ERROR_CODES.LINK_EXPIRED, "Setup link has expired", "expired-link", false, "Send a new link to continue the setup."),
  definition(CONNECTION_ERROR_CODES.LINK_INVALID, "Setup link is no longer valid", "expired-link"),
  definition(CONNECTION_ERROR_CODES.LINK_NOT_FOUND, "Setup link not found", "expired-link"),
  definition(CONNECTION_ERROR_CODES.RATE_LIMITED, "Too many attempts, try again later", "rate-limit", true),
  definition(CONNECTION_ERROR_CODES.NOT_AUTHORIZED, "Not authorized for this company", "authorization"),
  definition(CONNECTION_ERROR_CODES.INTERNAL_ERROR, "Something went wrong", "server", true),
  definition(CONNECTION_ERROR_CODES.NOT_IMPLEMENTED, "Not implemented yet", "server"),
  definition(CONNECTION_ERROR_CODES.NETWORK_ERROR, "Unable to reach the server. Please try again.", "transport", true),
  definition(CONNECTION_ERROR_CODES.UNKNOWN_ERROR, "Something went wrong. Please try again.", "server"),
];

const ERROR_BY_CODE = new Map<string, ConnectionErrorDefinition>(
  CONNECTION_ERRORS.map((error) => [error.code, error])
);

export const getConnectionErrorDefinition = (
  code: string
): ConnectionErrorDefinition | undefined => ERROR_BY_CODE.get(code);

export const RETRYABLE_CONNECTION_ERROR_CODES = new Set<string>(
  CONNECTION_ERRORS.filter((error) => error.retryable).map((error) => error.code)
);

export const CONNECTION_ERROR_FALLBACK_MESSAGE =
  "Something went wrong. Please try again.";

const CONNECTION_ERROR_FALLBACK_NEXT_STEP = "Please try again in a moment.";

// Gives `category` its first real use: every code gets a sensible second line even without its own.
const NEXT_STEP_BY_CATEGORY: Record<ConnectionErrorCategory, string> = {
  validation: "Check the details above and try again.",
  conflict: "Refresh this window and try again.",
  transport: CONNECTION_ERROR_FALLBACK_NEXT_STEP,
  server: CONNECTION_ERROR_FALLBACK_NEXT_STEP,
  "rate-limit": CONNECTION_ERROR_FALLBACK_NEXT_STEP,
  authorization: "Ask a workspace admin for access.",
  "expired-link": "Request a new setup link.",
};

export interface ConnectionErrorContent {
  title: string;
  description: string;
}

// First sentence, then the rest: a full stop followed by whitespace ("…@brand.com. Add it…").
const FIRST_SENTENCE = /^([\s\S]*?\.)\s+([\s\S]+)$/;

/**
 * The two lines the error panel shows. A plain string is a server-supplied message with no code
 * behind it. The failure messages read "What went wrong. What to do next.", so the first sentence
 * is the headline and the rest the next step; a one-sentence string gets the generic next step.
 */
export const connectionErrorContent = (
  error: { code?: string; message: string } | string
): ConnectionErrorContent => {
  if (typeof error === "string") {
    const [, title, nextStep] = FIRST_SENTENCE.exec(error.trim()) ?? [];
    if (!title) return { title: error, description: CONNECTION_ERROR_FALLBACK_NEXT_STEP };
    return { title, description: nextStep.trim() === ERROR_PANEL_DEFAULT_NOTE ? "" : nextStep };
  }
  const found = error.code ? getConnectionErrorDefinition(error.code) : undefined;
  return {
    title: error.message || found?.message || CONNECTION_ERROR_FALLBACK_MESSAGE,
    description:
      found?.nextStep ??
      (found ? NEXT_STEP_BY_CATEGORY[found.category] : CONNECTION_ERROR_FALLBACK_NEXT_STEP),
  };
};
